import { MATERIAL_TITLES } from './types';
// ── Stable identifiers ───────────────────────────────────────────────────────
export const MOCK_INSTRUCTOR_ID = 'usr-instructor';
export const MOCK_STUDENT_ID = 'usr-student-1';
// ── Batches ──────────────────────────────────────────────────────────────────
export const batches = [
    { id: 'batch-21', name: 'Batch 21', startDate: '2026-04-06', status: 'active' },
    { id: 'batch-22', name: 'Batch 22', startDate: '2026-09-07', status: 'upcoming' },
];
// ── Programs (instructor-side grouping of courses) ───────────────────────────
export const programs = [];
// ── Courses ──────────────────────────────────────────────────────────────────
export const courses = [
    {
        id: 'course-azure',
        slug: 'azure-cloud-security',
        title: 'CyberVault Azure Cloud Security',
        description: 'The 26-week Azure cloud security bootcamp.',
        status: 'active',
        order: 1,
        createdAt: '2026-01-01T08:00:00.000Z',
    },
    {
        id: 'course-ai',
        slug: 'build-secure-ai-agent',
        title: 'Build and Secure AI Agent',
        description: 'Design, build, and secure AI agents.',
        status: 'active',
        order: 2,
        createdAt: '2026-08-18T08:00:00.000Z',
    },
];
// ── Sections (big topics) — all under the Azure course ───────────────────────
export const sections = [
    {
        id: 'sec-1',
        order: 1,
        title: 'Azure Foundations',
        courseId: 'course-azure',
        interviewQuestions: [
            {
                question: 'Walk me through how you would structure an Azure environment for a new team using management groups, subscriptions and resource groups.',
                howAsked: 'A common opener for cloud roles — they want to see you reason about a governance hierarchy, not just recite definitions.',
                answer: 'Start at the top: a management group hierarchy mirrors the org (e.g. a root, then Platform and Landing Zones). Policies and RBAC assigned there inherit down. Subscriptions become the billing + isolation boundary — separate prod from non-prod so blast radius and cost are contained. Resource groups then group resources that share a lifecycle so you can deploy and delete them together. I’d apply Azure Policy at the management-group level for guardrails (allowed regions, required tags) so every new subscription is compliant by default.',
            },
            {
                question: 'What is the difference between an Azure AD (Entra ID) role and an Azure RBAC role?',
                howAsked: 'Probes whether you understand the two distinct control planes people constantly confuse.',
                answer: 'Entra ID roles (like Global Administrator) govern the directory itself — users, groups, app registrations. Azure RBAC roles (like Contributor or Reader) govern access to Azure resources under a subscription. They’re separate systems: being a Global Admin doesn’t grant resource access until you elevate, and a subscription Owner can’t manage the directory. Least privilege means assigning the narrowest role at the narrowest scope in whichever plane the task actually needs.',
            },
            {
                question: 'How do you keep secrets out of code and pipelines in Azure?',
                howAsked: 'A hands-on question — name specific services and describe a concrete setup you’ve built.',
                answer: 'Key Vault holds secrets, keys and certificates. Resources authenticate to it with a managed identity, so there’s no secret-zero in code or config. Access is granted with RBAC or vault access policies, scoped tightly, and every read is logged. In pipelines I reference the vault rather than storing values in variables, enable soft-delete and purge protection, and rotate on a schedule. The goal is that a leaked repo or build log never contains a usable credential.',
            },
        ],
    },
    {
        id: 'sec-2',
        order: 2,
        title: 'Threat Protection & SOC',
        courseId: 'course-azure',
        interviewQuestions: [
            {
                question: 'Tell me about a time you investigated a suspicious sign-in. How did you triage it?',
                howAsked: '"Tell me about a time" — structure it (situation → action → result) and show real tooling, not theory.',
                answer: 'I’d start from the Entra sign-in logs and the alert in Microsoft Defender / Sentinel: who, from where, which app, and the risk signals (impossible travel, unfamiliar location, MFA failures). I pivot in Sentinel with KQL to see the account’s recent activity and whether other users share the indicator. If it looks real, I contain — revoke sessions, force password reset, block the sign-in — then hunt for follow-on actions like mailbox rules or new app consents. I close by writing up the timeline and tuning the analytics rule to cut the false positives I saw.',
            },
            {
                question: 'What does a KQL query look like and when would you reach for one?',
                howAsked: 'Checks hands-on depth — they’ll be reassured if you can describe the shape of a real query.',
                answer: 'KQL is the query language for Sentinel and Log Analytics. A query pipes a table through operators: e.g. `SigninLogs | where ResultType != 0 | summarize failures = count() by UserPrincipalName, IPAddress | where failures > 10`. I reach for it during hunting and detection engineering — to slice logs for a pattern, build an analytics rule, or validate whether an alert reflects broader activity. Being fluent in where/summarize/join/project is what makes triage fast.',
            },
        ],
    },
    { id: 'sec-3', order: 3, title: 'Data, AI & DevSecOps', courseId: 'course-azure' },
    { id: 'sec-4', order: 4, title: 'Mastery & Capstone', courseId: 'course-azure' },
];
const MODULE_SPECS = [
    {
        order: 1,
        section: 1,
        title: 'Azure Administration Fundamentals',
        topics: ['Subscriptions & RBAC', 'Virtual Networks', 'Storage', 'Azure Policy'],
        status: 'published',
        materials: [
            { type: 'recording', durationSec: 3960 },
            { type: 'slides' },
            { type: 'transcript' },
            { type: 'summary', text: 'We covered subscriptions, RBAC scopes, VNets/NSGs and storage hardening. Key takeaway: apply least privilege at the narrowest scope that works.' },
        ],
    },
    {
        order: 2,
        section: 1,
        title: 'Identity & Conditional Access',
        topics: ['Entra ID', 'Conditional Access', 'MFA', 'Passwordless'],
        status: 'published',
        materials: [
            { type: 'recording', durationSec: 3720 },
            { type: 'slides' },
            { type: 'transcript' },
        ],
    },
    {
        order: 3,
        section: 2,
        title: 'Microsoft Defender & Endpoint Security',
        topics: ['Defender for Endpoint', 'Onboarding', 'ASR', 'EDR'],
        status: 'published',
        materials: [{ type: 'recording', durationSec: 2500 }, { type: 'slides' }],
    },
    {
        order: 4,
        section: 2,
        title: 'Microsoft Sentinel SOC Operations',
        topics: ['Workspace', 'Data connectors', 'KQL', 'Analytics rules'],
        status: 'published',
        materials: [
            { type: 'recording', durationSec: 5000 },
            { type: 'slides' },
            { type: 'transcript' },
            { type: 'summary', text: 'Sentinel workspace setup, connectors, KQL basics and analytics rules that raise incidents.' },
        ],
    },
    {
        order: 5,
        section: 2,
        title: 'Defender for Cloud & Posture Management',
        topics: ['Secure score', 'Compliance', 'Workload protection'],
        status: 'recording',
        materials: [{ type: 'recording', durationSec: 1900 }],
    },
    {
        order: 6,
        section: 3,
        title: 'Microsoft Purview & Information Protection',
        topics: ['Sensitivity labels', 'DLP', 'Insider risk'],
        status: 'recording',
        materials: [{ type: 'recording', durationSec: 1800 }],
    },
    {
        order: 7,
        section: 3,
        title: 'AI Security & Responsible AI',
        topics: ['AI workloads', 'Prompt injection', 'Model governance'],
        status: 'planned',
        materials: [],
    },
    {
        order: 8,
        section: 3,
        title: 'DevSecOps & Terraform',
        topics: ['IaC security', 'Pipeline gates', 'Policy as code'],
        status: 'planned',
        materials: [],
    },
    {
        order: 9,
        section: 4,
        title: 'Threat Hunting & KQL Mastery',
        topics: ['Advanced hunting', 'Detection engineering'],
        status: 'planned',
        materials: [],
    },
    {
        order: 10,
        section: 4,
        title: 'Capstone: Cloud SOC Project',
        topics: ['End-to-end project', 'Portfolio'],
        status: 'planned',
        materials: [],
    },
];
export const modules = MODULE_SPECS.map((s) => ({
    id: `mod-${s.order}`,
    order: s.order,
    title: s.title,
    topics: s.topics,
    releaseWeek: Math.max(0, s.order - 1),
    status: s.status,
    sectionId: `sec-${s.section}`,
}));
export const lessons = MODULE_SPECS.flatMap((s) => s.materials.map((m, i) => ({
    id: `lsn-${s.order}-${i + 1}`,
    moduleId: `mod-${s.order}`,
    title: MATERIAL_TITLES[m.type],
    type: m.type,
    streamUrl: m.text
        ? null
        : m.type === 'recording'
            ? `https://web.microsoftstream.com/embed/video/cvs-${s.order}-${i + 1}`
            : `https://example.blob.core.windows.net/course-content/cvs-${s.order}-${m.type}.pdf`,
    textContent: m.text ?? null,
    durationSec: m.durationSec ?? 0,
    order: i + 1,
})));
// ── Quizzes + questions ──────────────────────────────────────────────────────
export const quizzes = [
    { id: 'quiz-1', moduleId: 'mod-1', title: 'Azure Administration knowledge check' },
    { id: 'quiz-2', moduleId: 'mod-2', title: 'Identity & Conditional Access knowledge check' },
    { id: 'quiz-4', moduleId: 'mod-4', title: 'Sentinel SOC knowledge check' },
];
export const questions = [
    // quiz-1
    {
        id: 'q1-1',
        quizId: 'quiz-1',
        prompt: 'Which Azure RBAC scope is the broadest?',
        options: ['Resource', 'Resource group', 'Subscription', 'Management group'],
        correctIndex: 3,
        explanation: 'Management groups sit above subscriptions and let you apply governance across many subscriptions at once.',
    },
    {
        id: 'q1-2',
        quizId: 'quiz-1',
        prompt: 'What does a Network Security Group (NSG) primarily control?',
        options: ['DNS resolution', 'Inbound/outbound traffic filtering', 'Disk encryption', 'User sign-in'],
        correctIndex: 1,
        explanation: 'An NSG contains allow/deny rules that filter network traffic to and from Azure resources.',
    },
    {
        id: 'q1-3',
        quizId: 'quiz-1',
        prompt: 'Which storage redundancy option keeps copies in a second region?',
        options: ['LRS', 'ZRS', 'GRS', 'None of these'],
        correctIndex: 2,
        explanation: 'Geo-redundant storage (GRS) replicates data to a secondary region for regional disaster recovery.',
    },
    {
        id: 'q1-4',
        quizId: 'quiz-1',
        prompt: 'Azure Policy is mainly used to…',
        options: ['Assign user passwords', 'Enforce organizational standards and assess compliance', 'Host virtual machines', 'Stream video'],
        correctIndex: 1,
        explanation: 'Azure Policy evaluates resources against rules to enforce standards and report compliance at scale.',
    },
    {
        id: 'q1-5',
        quizId: 'quiz-1',
        prompt: 'The recommended approach to granting access is…',
        options: ['Assign Owner to everyone', 'Apply least privilege using built-in RBAC roles', 'Share the storage account key', 'Disable MFA for admins'],
        correctIndex: 1,
        explanation: 'Least privilege — granting only the access needed via scoped RBAC roles — limits blast radius.',
    },
    // quiz-2
    {
        id: 'q2-1',
        quizId: 'quiz-2',
        prompt: 'Conditional Access policies evaluate…',
        options: ['Only the password', 'Signals such as user, device, location and risk', 'Disk size', 'Billing usage'],
        correctIndex: 1,
        explanation: 'Conditional Access combines signals (user, device state, location, sign-in risk) to decide access.',
    },
    {
        id: 'q2-2',
        quizId: 'quiz-2',
        prompt: 'Which is a phishing-resistant, passwordless method?',
        options: ['SMS one-time code', 'FIDO2 security key', 'Security questions', 'Email magic link'],
        correctIndex: 1,
        explanation: 'FIDO2 keys are phishing-resistant and passwordless; SMS and email are weaker, phishable factors.',
    },
    {
        id: 'q2-3',
        quizId: 'quiz-2',
        prompt: "Blocking legacy authentication primarily mitigates…",
        options: ['Phishing-resistant MFA', 'Password-spray attacks over legacy protocols', 'Data exfiltration', 'DDoS'],
        correctIndex: 1,
        explanation: 'Legacy protocols (e.g. IMAP/SMTP basic auth) bypass MFA and are a common password-spray vector.',
    },
    {
        id: 'q2-4',
        quizId: 'quiz-2',
        prompt: 'Conditional Access requires at minimum which license?',
        options: ['Free', 'Entra ID P1', 'Microsoft 365 Basic', 'No license'],
        correctIndex: 1,
        explanation: 'Conditional Access requires at least Entra ID P1.',
    },
    // quiz-4
    {
        id: 'q4-1',
        quizId: 'quiz-4',
        prompt: 'Which KQL operator filters rows by condition?',
        options: ['summarize', 'where', 'project', 'render'],
        correctIndex: 1,
        explanation: '`where` filters rows; `project` selects columns; `summarize` aggregates; `render` visualizes.',
    },
    {
        id: 'q4-2',
        quizId: 'quiz-4',
        prompt: 'Sentinel analytics rules primarily generate…',
        options: ['Invoices', 'Incidents and alerts', 'Virtual machines', 'DNS zones'],
        correctIndex: 1,
        explanation: 'Analytics rules detect suspicious activity and raise alerts that group into incidents.',
    },
    {
        id: 'q4-3',
        quizId: 'quiz-4',
        prompt: "A data connector's job is to…",
        options: ['Bill customers', 'Ingest logs into the Log Analytics workspace', 'Encrypt disks', 'Rotate keys'],
        correctIndex: 1,
        explanation: 'Connectors bring data sources (Entra, Defender, syslog, etc.) into the workspace for analysis.',
    },
    {
        id: 'q4-4',
        quizId: 'quiz-4',
        prompt: 'To count rows grouped by a field in KQL you use…',
        options: ['take', 'summarize count() by …', 'sort', 'extend'],
        correctIndex: 1,
        explanation: '`summarize count() by Field` aggregates row counts per group.',
    },
];
// ── Labs (per class) + section projects ──────────────────────────────────────
export const labs = [
    {
        id: 'lab-1',
        title: 'Harden an Azure Storage Account',
        points: 100,
        env: 'Azure Sandbox',
        instructions: 'Disable public blob access, require HTTPS only, enable soft delete, and restrict network access to a selected VNet. Submit a screenshot of the configuration and the secure-score delta.',
        kind: 'lab',
        moduleId: 'mod-1',
        sectionId: null,
    },
    {
        id: 'lab-2',
        title: 'Build a Conditional Access Policy',
        points: 100,
        env: 'Entra ID tenant',
        instructions: 'Create a policy that requires MFA for all users accessing the Azure portal, excluding a break-glass account. Export the policy JSON and document the exclusions.',
        kind: 'lab',
        moduleId: 'mod-2',
        sectionId: null,
    },
    {
        id: 'lab-3',
        title: 'Write a Sentinel KQL Detection',
        points: 150,
        env: 'Sentinel workspace',
        instructions: 'Author a scheduled analytics rule that detects 10+ failed sign-ins followed by a success within 10 minutes. Submit the KQL and a screenshot of a generated incident.',
        kind: 'lab',
        moduleId: 'mod-4',
        sectionId: null,
    },
    {
        id: 'lab-4',
        title: 'Remediate Defender for Cloud Recommendations',
        points: 120,
        env: 'Azure Sandbox',
        instructions: 'Raise your secure score by at least 10 points by remediating three high-impact recommendations. Submit before/after secure-score screenshots.',
        kind: 'lab',
        moduleId: 'mod-5',
        sectionId: null,
    },
    {
        id: 'proj-1',
        title: 'Section project: Secure landing zone build-out',
        points: 150,
        env: 'Azure Sandbox',
        instructions: 'Design and deploy a small secure landing zone: management group + policy assignments, a hub VNet with NSGs, and RBAC following least privilege. Submit an architecture diagram and configuration screenshots. Do this after finishing the section’s classes.',
        kind: 'project',
        moduleId: null,
        sectionId: 'sec-1',
    },
    {
        id: 'proj-2',
        title: 'Section project: SOC incident response runbook',
        points: 200,
        env: 'Sentinel workspace',
        instructions: 'Build an end-to-end detection & response runbook: a custom analytics rule, an automation rule, and a written triage procedure. Submit the KQL, screenshots and the runbook document. Do this after finishing the section’s classes.',
        kind: 'project',
        moduleId: null,
        sectionId: 'sec-2',
    },
];
// ── Submissions (drives the grading queue) ───────────────────────────────────
export const submissions = [
    {
        id: 'sub-1',
        userId: 'usr-student-2',
        labId: 'lab-1',
        fileUrl: 'https://example.blob.core.windows.net/lab-submissions/sub-1.png',
        attachments: [{ url: 'https://example.blob.core.windows.net/lab-submissions/sub-1.png', name: 'storage-config.png', tag: 'Screenshot' }],
        status: 'submitted',
        grade: null,
        feedback: null,
        submittedAt: '2026-06-20T14:30:00.000Z',
    },
    {
        id: 'sub-2',
        userId: MOCK_STUDENT_ID,
        labId: 'lab-1',
        fileUrl: 'https://example.blob.core.windows.net/lab-submissions/sub-2.png',
        attachments: [
            { url: 'https://example.blob.core.windows.net/lab-submissions/sub-2.png', name: 'secure-score-before-after.png', tag: 'Screenshot' },
            { url: 'https://example.blob.core.windows.net/lab-submissions/sub-2-policy.json', name: 'storage-policy.json', tag: 'Config / Export' },
        ],
        status: 'graded',
        grade: 92,
        feedback: 'Excellent work. Network restriction was thorough; consider also enabling Microsoft Defender for Storage.',
        submittedAt: '2026-06-12T09:10:00.000Z',
    },
    {
        id: 'sub-3',
        userId: 'usr-student-3',
        labId: 'lab-2',
        fileUrl: 'https://example.blob.core.windows.net/lab-submissions/sub-3.zip',
        attachments: [{ url: 'https://example.blob.core.windows.net/lab-submissions/sub-3.zip', name: 'ca-policy-export.zip', tag: null }],
        status: 'submitted',
        grade: null,
        feedback: null,
        submittedAt: '2026-06-21T18:05:00.000Z',
    },
];
// ── Users (roster) ───────────────────────────────────────────────────────────
export const users = [
    {
        id: MOCK_INSTRUCTOR_ID,
        entraObjectId: 'oid-instructor',
        name: 'Lena Ortiz',
        email: 'lena@cybervaultschool.com',
        role: 'instructor',
        batchId: null,
        accessStatus: 'active',
        createdAt: '2026-01-15T08:00:00.000Z',
    },
    {
        id: MOCK_STUDENT_ID,
        entraObjectId: 'oid-student-1',
        name: 'Sam Carter',
        email: 'sam.carter@example.com',
        role: 'student',
        batchId: 'batch-21',
        accessStatus: 'active',
        createdAt: '2026-03-20T08:00:00.000Z',
    },
    {
        id: 'usr-student-2',
        entraObjectId: 'oid-student-2',
        name: 'Aisha Khan',
        email: 'aisha.khan@example.com',
        role: 'student',
        batchId: 'batch-21',
        accessStatus: 'active',
        createdAt: '2026-03-21T08:00:00.000Z',
    },
    {
        id: 'usr-student-3',
        entraObjectId: 'oid-student-3',
        name: 'Diego Martins',
        email: 'diego.martins@example.com',
        role: 'student',
        batchId: 'batch-21',
        accessStatus: 'active',
        createdAt: '2026-03-22T08:00:00.000Z',
    },
    {
        id: 'usr-student-4',
        entraObjectId: 'oid-student-4',
        name: 'Priya Nair',
        email: 'priya.nair@example.com',
        role: 'student',
        batchId: 'batch-21',
        accessStatus: 'pending',
        createdAt: '2026-06-18T08:00:00.000Z',
    },
    {
        id: 'usr-student-5',
        entraObjectId: 'oid-student-5',
        name: 'Tom Becker',
        email: 'tom.becker@example.com',
        role: 'student',
        batchId: 'batch-21',
        accessStatus: 'pending',
        createdAt: '2026-06-19T08:00:00.000Z',
    },
    {
        id: 'usr-student-6',
        entraObjectId: 'oid-student-6',
        name: 'Yuki Tanaka',
        email: 'yuki.tanaka@example.com',
        role: 'student',
        batchId: 'batch-21',
        accessStatus: 'revoked',
        createdAt: '2026-03-25T08:00:00.000Z',
    },
    {
        id: 'usr-student-7',
        entraObjectId: 'oid-student-7',
        name: 'Omar Farouk',
        email: 'omar.farouk@example.com',
        role: 'student',
        batchId: 'batch-22',
        accessStatus: 'pending',
        createdAt: '2026-06-15T08:00:00.000Z',
    },
];
// ── Enrollments — every student is in the Azure course; the AI course starts empty.
// (Mirrors the production backfill: enroll all students regardless of accessStatus,
// since the global gate still blocks pending/revoked members.)
export const enrollments = users
    .filter((u) => u.role === 'student')
    .map((u) => ({
    id: `enr_${u.id}_course-azure`,
    userId: u.id,
    courseId: 'course-azure',
    status: 'active',
    enrolledAt: u.createdAt,
}));
// ── Progress for the primary mock student ────────────────────────────────────
function completed(lessonId, durationSec, idx) {
    return {
        id: `prog-${idx}`,
        userId: MOCK_STUDENT_ID,
        lessonId,
        completed: true,
        lastPositionSec: durationSec,
        watchedSec: durationSec, // finished the class → watched the whole recording
        updatedAt: '2026-06-18T10:00:00.000Z',
    };
}
export const progress = [
    // Class 1 complete (all four materials)
    completed('lsn-1-1', 3960, 1),
    completed('lsn-1-2', 0, 2),
    completed('lsn-1-3', 0, 3),
    completed('lsn-1-4', 0, 4),
    // Class 2 partially complete (2 of 3 materials)
    completed('lsn-2-1', 3720, 5),
    completed('lsn-2-2', 0, 6),
    // Class 3 just started — recording done, slides in progress
    completed('lsn-3-1', 2500, 7),
    {
        id: 'prog-8',
        userId: MOCK_STUDENT_ID,
        lessonId: 'lsn-3-2',
        completed: false,
        lastPositionSec: 0,
        watchedSec: 0,
        updatedAt: '2026-06-21T19:00:00.000Z',
    },
];
export const quizAttempts = [
    {
        id: 'qa-1',
        userId: MOCK_STUDENT_ID,
        quizId: 'quiz-1',
        score: 0.8,
        answers: [3, 1, 2, 1, 0],
        submittedAt: '2026-05-02T12:00:00.000Z',
    },
];
// ── Badges catalog ───────────────────────────────────────────────────────────
export const badges = [
    { id: 'badge-first-steps', name: 'First Steps', description: 'Completed your first lesson', icon: 'Footprints' },
    { id: 'badge-week-warrior', name: 'Streak Star', description: '7-day learning streak', icon: 'Fire' },
    { id: 'badge-quiz-ace', name: 'Quiz Ace', description: 'Scored 80%+ on a knowledge check', icon: 'Medal' },
    { id: 'badge-lab-master', name: 'Lab Master', description: 'Graded 90%+ on a lab', icon: 'Flask' },
    { id: 'badge-soc-analyst', name: 'Section Finisher', description: 'Completed every class in a section', icon: 'ShieldCheck' },
];
// ── Dashboard summaries (per user) ───────────────────────────────────────────
export const dashboards = {
    [MOCK_STUDENT_ID]: {
        streakDays: 12,
        weeklyGoal: 5,
        weeklyCompleted: 3,
        topicMastery: [
            { topic: 'Azure Admin', mastery: 85 },
            { topic: 'Identity', mastery: 60 },
            { topic: 'Defender', mastery: 35 },
            { topic: 'Sentinel SOC', mastery: 10 },
            { topic: 'Defender for Cloud', mastery: 0 },
        ],
        earnedBadgeIds: ['badge-first-steps', 'badge-week-warrior', 'badge-quiz-ace'],
        continueLessonId: 'lsn-3-2',
    },
};
// ── Interview package ────────────────────────────────────────────────────────
export const interviewPackages = [
    {
        id: 'pkg-cloud-sec-eng',
        role: 'Cloud Security Engineer',
        company: 'Microsoft Partner SOC',
        studyGuide: [
            {
                id: 'sg-1',
                concept: 'Conditional Access',
                tools: ['Microsoft Entra ID', 'Conditional Access', 'Identity Protection'],
                whyItMatters: 'It is the policy engine that enforces Zero Trust at sign-in — interviewers probe how you balance security and usability.',
            },
            {
                id: 'sg-2',
                concept: 'SIEM detection engineering',
                tools: ['Microsoft Sentinel', 'KQL', 'Analytics rules'],
                whyItMatters: 'Writing high-signal detections with low false positives is the core day-to-day of a SOC role.',
            },
            {
                id: 'sg-3',
                concept: 'Cloud security posture management',
                tools: ['Defender for Cloud', 'Secure Score', 'Azure Policy'],
                whyItMatters: 'Demonstrates you can measurably reduce risk across a subscription, not just configure point solutions.',
            },
            {
                id: 'sg-4',
                concept: 'Incident response',
                tools: ['Defender XDR', 'Sentinel incidents', 'Playbooks'],
                whyItMatters: 'Shows you can triage, contain, and document — the difference between an analyst and an engineer.',
            },
        ],
        starQA: [
            {
                id: 'star-1',
                competency: 'Incident response',
                question: 'Tell me about a time you responded to a security incident.',
                star: {
                    situation: 'During a lab simulation, Sentinel raised an incident for impossible-travel sign-ins on a privileged account.',
                    task: 'Determine whether it was a true positive and contain it without disrupting legitimate work.',
                    action: 'Correlated sign-in logs, confirmed the foreign IP was a known malicious ASN, revoked sessions, forced password reset, and added a Conditional Access location policy.',
                    result: 'Contained the account in under 15 minutes and added a detection that cut similar false positives by tuning the rule.',
                },
                modelAnswer: 'In a Sentinel lab I owned an impossible-travel incident on a Global Admin. I triaged by correlating the AADSignInLogs with threat intel, confirmed the source IP belonged to a flagged ASN, and treated it as a true positive. I revoked active sessions and required a secure password reset, then closed the gap by adding a Conditional Access policy blocking sign-ins outside approved regions. Finally I tuned the analytics rule with a trusted-IP watchlist, which reduced false positives. The result: contained in ~15 minutes with a durable preventive control.',
            },
            {
                id: 'star-2',
                competency: 'Automation',
                question: 'Describe a time you automated a repetitive security task.',
                star: {
                    situation: 'Manually remediating Defender for Cloud recommendations across resources was slow.',
                    task: 'Reduce the time to remediate common misconfigurations.',
                    action: 'Built an Azure Policy with deployIfNotExists to enforce HTTPS-only on storage accounts and a Logic App playbook to notify owners.',
                    result: 'New non-compliant resources self-remediated and secure score rose by 12 points.',
                },
                modelAnswer: 'Remediating storage misconfigurations by hand did not scale, so I wrote an Azure Policy using deployIfNotExists to enforce HTTPS-only and secure transfer, paired with a Sentinel/Logic App playbook that pinged resource owners on drift. After assignment, new resources self-corrected and our secure score climbed 12 points within a week — turning a manual checklist into a guardrail.',
            },
            {
                id: 'star-3',
                competency: 'Communication',
                question: 'Give an example of explaining a security risk to a non-technical stakeholder.',
                star: {
                    situation: 'Leadership wanted to allow legacy authentication for a vendor app.',
                    task: 'Explain the risk and propose a safer path.',
                    action: 'Framed legacy auth as a door that bypasses the MFA lock, quantified the password-spray exposure, and proposed an app proxy alternative.',
                    result: 'Leadership approved keeping legacy auth blocked and funded the app proxy.',
                },
                modelAnswer: 'A vendor app pushed us to re-enable legacy authentication. Rather than lead with protocols, I explained it as "a side door that skips the MFA lock we paid for," and showed our own sign-in logs of blocked password-spray attempts against those endpoints. I offered an Application Proxy path that kept MFA intact. Leadership chose to keep legacy auth blocked and funded the proxy — a security win achieved through plain-language framing and evidence.',
            },
        ],
        projects: [
            {
                id: 'proj-1',
                title: 'Cloud SOC in a Box',
                summary: 'A deployable Sentinel workspace with connectors, 10 analytics rules, and automated playbooks.',
                skills: ['Sentinel', 'KQL', 'Logic Apps', 'Bicep'],
                repoUrl: null,
                highlights: [
                    'Detections mapped to MITRE ATT&CK',
                    'Automated incident enrichment playbook',
                    'One-command Bicep deployment',
                ],
            },
            {
                id: 'proj-2',
                title: 'Zero Trust Identity Baseline',
                summary: 'A Conditional Access baseline with break-glass design and reporting.',
                skills: ['Entra ID', 'Conditional Access', 'PowerShell'],
                repoUrl: null,
                highlights: ['Phishing-resistant MFA', 'Break-glass account runbook', 'Policy-as-code export'],
            },
            {
                id: 'proj-3',
                title: 'Secure Score Automation',
                summary: 'Policy-as-code that enforces storage and network baselines and tracks secure-score delta.',
                skills: ['Defender for Cloud', 'Azure Policy', 'Bicep'],
                repoUrl: null,
                highlights: ['deployIfNotExists guardrails', 'Drift notifications', 'Secure-score dashboard'],
            },
        ],
    },
];
//# sourceMappingURL=mock-data.js.map